
"use client"

import { useState } from "react"
import { useRouter } from "next/navigation"
import { useAuth } from "@/contexts/AuthContext"
import { toast } from "sonner"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { PinDialog } from "@/components/PinDialog"
import { Calculator, ChefHat, Monitor, Settings, Store, Loader2, Ticket } from "lucide-react"


export default function HomePage() {
  const router = useRouter();
  const { login } = useAuth();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<'pos' | 'kds' | 'admin' | 'screen' | 'reception' | null>(null);
  const [targetPath, setTargetPath] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleMenuClick = async (role: 'pos' | 'kds' | 'admin' | 'screen' | 'reception', path: string) => {
    if (isLoading) return;

    if (role === 'screen') {
      setIsLoading(true);
      toast.info("スクリーン画面に接続しています...");
      const success = await login({ role, pin: 'screen' }); // PIN is a dummy value
      if (success) {
        router.push(path);
      } else {
        toast.error("スクリーン画面への接続に失敗しました。");
        setIsLoading(false);
      }
    } else {
      setSelectedRole(role);
      setTargetPath(path);
      setDialogOpen(true);
    }
  };

  const menuItems = [
    {
      title: "レジ（POS）",
      description: "商品販売・会計処理",
      icon: Calculator,
      path: "/pos",
      role: "pos" as const,
      color: "bg-blue-500",
    },
    {
      title: "受付所パネル",
      description: "前売り注文の引換処理",
      icon: Ticket,
      path: "/reception",
      role: "reception" as const,
      color: "bg-red-500",
    },
    {
      title: "調理画面（KDS）",
      description: "注文管理・調理進捗",
      icon: ChefHat,
      path: "/kds",
      role: "kds" as const,
      color: "bg-green-500",
    },
    {
      title: "受け渡し①",
      description: "割り当てられた注文をシングルタスクで処理",
      icon: ChefHat,
      path: "/handover/1",
      role: "kds" as const,
      color: "bg-teal-500",
    },
    {
      title: "受け渡し②",
      description: "割り当てられた注文をシングルタスクで処理",
      icon: ChefHat,
      path: "/handover/2",
      role: "kds" as const,
      color: "bg-cyan-500",
    },
    {
      title: "お客様表示",
      description: "呼び出し番号表示",
      icon: Monitor,
      path: "/screen",
      role: "screen" as const,
      color: "bg-purple-500",
    },
    {
      title: "管理画面",
      description: "在庫・設定管理",
      icon: Settings,
      path: "/admin",
      role: "admin" as const,
      color: "bg-orange-500",
    },
  ]

  return (
    <>
      <div className="min-h-screen bg-background p-4 md:p-6 lg:p-8">
        {isLoading && (
          <div className="fixed inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center z-50">
            <Loader2 className="h-8 w-8 animate-spin" />
            <p className="ml-4 text-lg">接続中...</p>
          </div>
        )}
        <div className="max-w-5xl mx-auto">
          {/* ヘッダー */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Store className="h-8 w-8 text-primary" />
              <h1 className="text-3xl font-bold text-foreground">Chickeeeen</h1>
            </div>
            <p className="text-muted-foreground text-lg">レジ・伝達表示・管理統合システム</p>
          </div>

          {/* メニューグリッド */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {menuItems.map((item) => {
              const Icon = item.icon
              return (
                <Card
                  key={item.path}
                  className="hover:shadow-lg transition-all duration-200 cursor-pointer group"
                  onClick={() => handleMenuClick(item.role, item.path)}
                >
                  <CardHeader className="pb-4">
                    <div className="flex items-center gap-4">
                      <div
                        className={`p-3 rounded-xl ${item.color} text-white group-hover:scale-110 transition-transform duration-200`}
                      >
                        <Icon className="h-6 w-6" />
                      </div>
                      <div>
                        <CardTitle className="text-xl">{item.title}</CardTitle>
                        <CardDescription className="text-base min-h-[2.5rem]">{item.description}</CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <Button
                      className="w-full h-12 text-base font-medium"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleMenuClick(item.role, item.path)
                      }}
                    >
                      開く
                    </Button>
                  </CardContent>
                </Card>
              )
            })}
          </div>

          {/* フッター */}
          <div className="text-center mt-12 text-sm text-muted-foreground">
            <p>6C学友祭POSシステム v4.0 | 2025</p>
            <p>PWA対応</p>
          </div>
        </div>
      </div>
      
      <PinDialog 
        open={dialogOpen} 
        onOpenChange={setDialogOpen} 
        role={selectedRole} 
        targetPath={targetPath} 
      />
    </>
  )
}
